import { cn } from "../../lib/utils";

interface ProgressRingProps {
  progress: number; // 0-100
  size?: number;
  strokeWidth?: number;
  className?: string;
  showLabel?: boolean;
  variant?: 'primary' | 'success' | 'warning' | 'danger';
}

export function ProgressRing({
  progress,
  size = 120,
  strokeWidth = 8,
  className,
  showLabel = true,
  variant = 'primary',
}: ProgressRingProps) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  const colorClasses = {
    primary: 'stroke-brand-primary',
    success: 'stroke-brand-success',
    warning: 'stroke-brand-warning',
    danger: 'stroke-brand-danger',
  };

  const glowClasses = {
    primary: 'drop-shadow-[0_0_8px_hsl(var(--brand-primary)/0.5)]',
    success: 'drop-shadow-[0_0_8px_hsl(var(--brand-success)/0.5)]',
    warning: 'drop-shadow-[0_0_8px_hsl(var(--brand-warning)/0.5)]',
    danger: 'drop-shadow-[0_0_8px_hsl(var(--brand-danger)/0.5)]',
  };

  return (
    <div className={cn("relative inline-flex items-center justify-center", className)}>
      <svg
        width={size}
        height={size}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          className="text-muted/30"
        />

        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className={cn(
            "progress-ring transition-all duration-500 ease-out",
            colorClasses[variant],
            progress > 0 && glowClasses[variant]
          )}
        />
      </svg>

      {/* Label */}
      {showLabel && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className="font-bold text-foreground"
            style={{ fontSize: size <= 64 ? '0.875rem' : size <= 80 ? '1.25rem' : '1.5rem' }}
          >
            {Math.round(progress)}%
          </span>
        </div>
      )}
    </div>
  );
}

